import React, { useCallback, useEffect, useRef, useState } from "react";
import { Mail, Send } from "lucide-react";
import "./inbox.css";

// Client portal "Messages" tab: the clinic's conversation with the agency
// team. GET/POST /api/client/messages - the backend derives the clinic from
// the session token, so nothing here (or in the request) selects a client.

const CHANNEL_LABELS = { portal: "Portal", sms: "SMS", whatsapp: "WhatsApp", email: "Email" };

export default function ClientPortalMessages({ api, showNotice }) {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // ClientPortal recreates `api`/`showNotice` every render; keep them in
  // refs so loading never loops on a re-render.
  const apiRef = useRef(api);
  const noticeRef = useRef(showNotice);
  apiRef.current = api;
  noticeRef.current = showNotice;

  const load = useCallback(async () => {
    try {
      const res = await apiRef.current.get("/api/client/messages");
      // Oldest first for reading as a conversation.
      setMessages([...(res.data?.messages || [])].reverse());
    } catch (err) {
      noticeRef.current?.(err.response?.data?.detail || "Failed to load messages", "error");
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      await apiRef.current.post("/api/client/messages", { body });
      setBody("");
      noticeRef.current?.("Message sent to your MedSpaBookings team", "success");
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      noticeRef.current?.(typeof detail === "string" ? detail : "Message could not be sent", "error");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="page-stack" data-testid="client-messages">
      <section className="page-header">
        <h2>Messages</h2>
        <p className="muted">Message your MedSpaBookings account team. Replies appear here.</p>
      </section>

      <section className="panel glass-panel inbox-thread-view">
        <div className="inbox-messages" data-testid="client-messages-list">
          {loaded && !messages.length && <div className="empty-cell">No messages yet</div>}
          {messages.map((msg) => {
            // From the clinic's point of view, staff replies are incoming.
            const fromTeam = msg.direction === "outbound";
            const via = msg.delivery_channel || msg.channel_key || (msg.channel || "").toLowerCase();
            return (
              <div key={msg.id} className={`inbox-bubble ${fromTeam ? "inbound" : "outbound"}`}>
                {msg.body}
                <div className="inbox-meta">
                  <Mail size={12} />
                  <span>{fromTeam ? (msg.from_name || "MedSpaBookings team") : (msg.author_name || msg.from_name || "You")}</span>
                  {CHANNEL_LABELS[via] && <span className="inbox-badge">{CHANNEL_LABELS[via]}</span>}
                  <span>{msg.created_at ? new Date(msg.created_at).toLocaleString() : ""}</span>
                </div>
              </div>
            );
          })}
        </div>
        <div className="inbox-reply" data-testid="client-message-compose">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={5000}
            placeholder="Write a message to your account team…"
            data-testid="client-message-body"
          />
          <div className="inbox-reply-row" style={{ justifyContent: "flex-end" }}>
            <button className="primary-btn" type="button" onClick={send} disabled={sending || !body.trim()} data-testid="client-message-send">
              <Send size={15} /> {sending ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
